function getBufferedAhead(video) {
  if (!video || !video.buffered || video.buffered.length === 0) return null;

  try {
    // 只看目前播放點之後還有多少可播放秒數，不使用已經播放過的 buffer。
    return Math.max(
      0,
      video.buffered.end(video.buffered.length - 1) - video.currentTime,
    );
  } catch (error) {
    return null;
  }
}

export function diagnosePlaybackFailure({
  video,
  reason = 'unknown',
  mediaWasEmptied = false,
}) {
  const bufferedAhead = getBufferedAhead(video);
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const hasLittleBuffer = bufferedAhead !== null && bufferedAhead <= 1.5;

  // 下載錯誤或前置 buffer 幾乎用完，優先歸類為使用者連線／CDN 傳輸不穩。
  if (
    isOffline
    || reason === 'network-error'
    || ((reason === 'waiting' || reason === 'stalled')
      && hasLittleBuffer)
  ) {
    return {
      category: 'NETWORK_UNSTABLE',
      title: '影音連線不穩',
      message: '目前影片資料下載不穩，可能與 Wi-Fi 或行動網路連線有關。',
      action: '請先確認其他網頁可以正常開啟；若可以，請切換 Wi-Fi/行動網路後重新播放。',
      bufferedAhead,
    };
  }

  // emptied 是瀏覽器真的清空媒體資源的訊號，常見於手機記憶體壓力或切換 App。
  if (
    mediaWasEmptied
    || (reason === 'visibility' && video && video.readyState === 0)
  ) {
    return {
      category: 'DEVICE_RESOURCE',
      title: '瀏覽器暫時無法維持影片資源',
      message: '瀏覽器可能暫時釋放了影音資源，常見於手機資源不足或切換 App/分頁後。',
      action: '請關閉其他 App 或分頁，重新整理課程頁面後再試；若仍反覆發生，最後再清除本網站快取並重新登入。',
      bufferedAhead,
    };
  }

  // buffer 仍足夠卻發生 media error，比較接近解碼器或播放器狀態中斷。
  if (
    reason === 'media-error'
    || (
      video
      && video.readyState >= 3
      && bufferedAhead !== null
      && bufferedAhead >= 3
    )
  ) {
    return {
      category: 'PLAYER_STATE',
      title: '影片播放暫時中斷',
      message: '影片目前無法順利繼續播放，可能與目前的網路、瀏覽器或裝置狀態有關。',
      action: '請重新整理課程頁面後再試；若仍反覆發生，可關閉其他分頁或改用其他瀏覽器再試。',
      bufferedAhead,
    };
  }

  return {
    category: 'UNKNOWN',
    title: '影片暫時無法播放',
    message: '系統已嘗試恢復影片，但目前無法確認是網路、裝置或播放器狀態造成。',
    action: '請先確認網路並重新整理課程頁面後再試；若仍反覆發生，最後再清除本網站快取並重新登入。',
    bufferedAhead,
  };
}

export function showPlaybackDiagnosis(vm, options = {}) {
  if (!vm || vm.playbackDiagnosisShown || typeof vm.$alert !== 'function') return;

  const viewModel = vm;
  const diagnosis = diagnosePlaybackFailure(options);
  viewModel.playbackDiagnosisShown = true;

  // 對學生只呈現可採取的處理方式，不直接宣稱一定是系統或裝置責任。
  const message = [
    `初步判斷：${diagnosis.title}`,
    diagnosis.message,
    `建議：${diagnosis.action}`,
  ].join('\n\n');

  viewModel.$alert(message, '影片播放問題', {
    confirmButtonText: '知道了',
    type: 'warning',
    callback: () => {
      viewModel.playbackDiagnosisShown = false;
    },
  });
}

export function showPlaybackStatusNotice(vm, options = {}) {
  if (!vm || typeof vm.$message !== 'function') return null;

  const viewModel = vm;
  const diagnosis = diagnosePlaybackFailure(options);
  // 短暫緩衝只用非阻斷訊息；完整錯誤彈窗由恢復失敗流程負責。
  const noticeMessage = {
    NETWORK_UNSTABLE: '目前網路較慢，影片正在緩衝中，請稍候。',
    DEVICE_RESOURCE: '影片目前正在恢復，請稍候。',
    PLAYER_STATE: '影片畫面暫時沒有更新，請稍候。',
    UNKNOWN: '影片暫時沒有回應，請稍候。',
  }[diagnosis.category] || '影片暫時沒有回應，請稍候。';
  let messageInstance = null;
  messageInstance = viewModel.$message({
    showClose: true,
    type: 'warning',
    message: noticeMessage,
    duration: 5000,
    onClose: () => {
      if (viewModel.playbackNetworkNoticeInstance === messageInstance) {
        viewModel.playbackNetworkNoticeInstance = null;
      }
    },
  });

  return messageInstance;
}
